import { useParams, useRouterState } from '@tanstack/react-router';
import { bbox } from '@turf/turf';
import type { MultiPoint, MultiPolygon } from 'geojson';
import { useEffect, useMemo, useRef } from 'react';
import { useMap, type ViewState } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { MapComponent as MapboxMap } from '~/components2/Map';
import {
  consumeReturnViewport,
  historyEntryKeySelector,
  layerLibraryReturnKey,
  peekReturnViewport,
} from '~/components2/Map/helpers/layerLibraryReturnViewport';
import rootStore from '~/store';
import { onRootBasemapChange } from '../../common/helpers';
import newDownloadStore from '../new-download/store';
import EditorSurface from './EditorSurface';
import store from './projectDetailsMapStore';
import Surface from './Surface';

interface ProjectDetailsMapProps {
  projectGeometry: MultiPolygon;
  projectCircle?: MultiPoint;
  children?: any;
}

export default function ProjectDetailsMap({ projectGeometry, projectCircle, children }: ProjectDetailsMapProps) {
  const { projectDetailsMap } = useMap();
  const { inNewDownloadMode } = useSnapshot(newDownloadStore);
  const { projectId } = useParams({ strict: false }) as { projectId?: string };
  const historyKey = useRouterState({ select: historyEntryKeySelector });
  const returnKey = projectId ? layerLibraryReturnKey(projectId) : undefined;

  // Camera stashed by the Layer Library round-trip; reused map instances ignore
  // `initialViewState`, so the effect below also applies it via jumpTo.
  const restoredInitialViewState = useMemo(
    () => (returnKey ? peekReturnViewport(returnKey, historyKey) : undefined),
    [returnKey, historyKey],
  );

  const pendingRestoreRef = useRef<ViewState | null>(null);
  const restoredBoundsKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const bounds = bbox(projectGeometry) as [number, number, number, number];
    const boundsKey = JSON.stringify(bounds);
    const stashed = returnKey ? consumeReturnViewport(returnKey, historyKey) : undefined;
    if (stashed) {
      pendingRestoreRef.current = stashed;
      restoredBoundsKeyRef.current = boundsKey;
    }
    // Skip the project fit while a restore holds this geometry: remount refetches can
    // change the geometry's identity without changing its value.
    if (restoredBoundsKeyRef.current === boundsKey) {
      const restore = pendingRestoreRef.current;
      if (restore && projectDetailsMap) {
        projectDetailsMap.jumpTo({
          center: [restore.longitude, restore.latitude],
          zoom: restore.zoom,
          bearing: restore.bearing,
          pitch: restore.pitch,
          padding: restore.padding,
        });
        pendingRestoreRef.current = null;
      }
      return;
    }
    pendingRestoreRef.current = null;
    restoredBoundsKeyRef.current = null;
    projectDetailsMap?.fitBounds(bounds, { padding: 100 });
  }, [projectGeometry, projectDetailsMap, returnKey, historyKey]);

  return (
    // The canvas inside is its own "Interactive map" region.
    <section aria-label="Project map" className="h-full w-full">
      <MapboxMap
        id="projectDetailsMap"
        store={store}
        mapStyle={rootStore.preferredBasemap}
        onBasemapChange={onRootBasemapChange}
        showLayers={true}
        showLayerPicker={!inNewDownloadMode}
        showLegend={true}
        showBasemapPicker={true}
        showZoomButtons={true}
        showScale={true}
        printTool={true}
        allowRotation={true}
        measureTool={true}
        allowHistoricalImagery={true}
        allowSplitView={false}
        showLayerDetails={true}
        projectGeometry={projectGeometry}
        projectCircle={projectCircle}
        initialViewState={
          restoredInitialViewState ?? {
            bounds: bbox(projectGeometry) as [number, number, number, number],
            fitBoundsOptions: { padding: 100 },
          }
        }
        inNewDownloadMode={inNewDownloadMode}
        projectId={projectId}
      >
        {children}
        <Surface />
        <EditorSurface />
      </MapboxMap>
    </section>
  );
}
